import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Coins, Package, Gift, Sparkles, Zap, 
  Crown, Star, Clock, Play, CheckCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import GodTierPackOpening from '../components/GodTierPackOpening';
import { useAuth } from '../providers/AuthProvider';
import { saveCard } from '../utils/inventoryManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token bundles for purchase with real money (future Solana integration)
// PHASE 11: REBRAND - $SLAP Token Bundles with USD FIRST Pricing
const tokenBundles = [
  { id: 'tester', tokens: 250, price: 0.02, priceDisplay: '0.02 SOL', usdPrice: '$3', bonus: 0, popular: true, label: '⚡ TRY IT OUT' },
  { id: 'starter', tokens: 600, price: 0.05, priceDisplay: '0.05 SOL', usdPrice: '$7.50', bonus: 50, popular: false },
  { id: 'pro', tokens: 1300, price: 0.1, priceDisplay: '0.1 SOL', usdPrice: '$15', bonus: 100, popular: false },
  { id: 'whale', tokens: 7000, price: 0.5, priceDisplay: '0.5 SOL', usdPrice: '$75', bonus: 500, popular: false },
];

// Pack types with USD equivalent - $SLAP ONLY - USD FIRST DISPLAY
const packTypes = [
  { 
    id: 'basic', 
    name: 'Basic Pack', 
    cost: 50,
    usd: '$0.25',
    currency: 'slap',
    description: 'Standard odds. Good for beginners!',
    rarityBoost: null,
    icon: Package,
    gradient: 'from-cyan-600 to-teal-600',
    currencyColor: '#FFD700',
    glowColor: 'rgba(0,255,255,0.5)',
  },
  { 
    id: 'premium', 
    name: 'Premium Pack', 
    cost: 250,
    usd: '$1.25',
    currency: 'slap',
    description: '2x chance for Rare+',
    rarityBoost: 'rare',
    icon: Sparkles,
    gradient: 'from-blue-600 to-cyan-600',
    currencyColor: '#FFD700',
    glowColor: 'rgba(59,130,246,0.6)',
  },
  { 
    id: 'elite', 
    name: 'Elite Pack', 
    cost: 500,
    usd: '$2.50',
    currency: 'slap',
    description: '3x chance for Epic+',
    rarityBoost: 'epic',
    icon: Zap,
    gradient: 'from-purple-600 to-pink-600',
    currencyColor: '#FFD700',
    glowColor: 'rgba(168,85,247,0.6)',
  },
  { 
    id: 'legendary', 
    name: 'Legendary Pack', 
    cost: 1000,
    usd: '$5.00',
    currency: 'slap',
    description: 'Guaranteed Epic or better!',
    rarityBoost: 'legendary',
    icon: Crown,
    gradient: 'from-yellow-500 to-orange-600',
    currencyColor: '#FFD700',
    glowColor: 'rgba(255,215,0,0.7)',
  },
];

const WatchAdOverlay = ({ onComplete }) => {
  const [timeLeft, setTimeLeft] = useState(3);
  
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      onComplete();
    }
  }, [timeLeft, onComplete]);
  
  return (
    <motion.div
      className="fixed inset-0 z-[900] flex items-center justify-center bg-black/95"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="text-center">
        <motion.div
          className="w-32 h-32 mx-auto mb-8 rounded-full border-4 border-neon-green flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <Play className="w-16 h-16 text-neon-green" />
        </motion.div>
        
        <h2 className="font-anton text-4xl text-white mb-4">WATCHING AD...</h2>
        
        <div className="relative w-64 h-4 bg-white/20 rounded-full mx-auto overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-neon-green rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 3, ease: 'linear' }}
          />
        </div>
        
        <p className="font-space text-2xl text-neon-green mt-4">{timeLeft}s</p>
        <p className="font-space text-sm text-white/40 mt-2">Free Shards incoming!</p>
      </div>
    </motion.div>
  );
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { walletAddress, isAuthenticated, user, refreshUser } = useAuth();
  const SESSION_ID = walletAddress || 'default_session';
  
  const [activeTab, setActiveTab] = useState('packs');
  const [showWatchAd, setShowWatchAd] = useState(false);
  const [pulledCard, setPulledCard] = useState(null);
  const [showPackAnimation, setShowPackAnimation] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [freeRewardCooldown, setFreeRewardCooldown] = useState(0);
  const [packOpeningError, setPackOpeningError] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  
  useEffect(() => {
    refreshUser();
    checkFreeRewardCooldown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const checkFreeRewardCooldown = () => {
    const lastFree = localStorage.getItem('lastFreeReward');
    if (lastFree) {
      const elapsed = Date.now() - parseInt(lastFree);
      const remaining = Math.max(0, 300000 - elapsed); // 5 min cooldown
      setFreeRewardCooldown(Math.ceil(remaining / 1000));
      
      if (remaining > 0) {
        const interval = setInterval(() => {
          setFreeRewardCooldown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return () => clearInterval(interval);
      }
    }
  };
  
  const handleBuyTokens = (bundle) => {
    toast.info('SOL Token purchase coming soon!', {
      description: `${bundle.tokens} + ${bundle.bonus} bonus $SLAP for ${bundle.priceDisplay}`,
    });
  };
  
  // Generate fallback card if backend times out
  const generateFallbackCard = (packId) => {
    const rarities = ['common', 'rare', 'epic', 'legendary'];
    const classes = ['meme', 'tech', 'politics'];
    const names = ['Mystery Hero', 'Unknown Warrior', 'Shadow Fighter', 'Cyber Punk', 'Neon Ghost'];
    
    // Better odds for premium packs
    let rarityIndex = 0;
    if (packId === 'premium') rarityIndex = Math.random() < 0.5 ? 1 : 0;
    else if (packId === 'elite') rarityIndex = Math.random() < 0.3 ? 2 : 1;
    else if (packId === 'legendary') rarityIndex = Math.random() < 0.5 ? 3 : 2;
    else rarityIndex = Math.random() < 0.7 ? 0 : 1;
    
    return {
      id: `fallback_${Date.now()}`,
      character_name: names[Math.floor(Math.random() * names.length)],
      title: 'Emergency Generated Card',
      rarity: rarities[rarityIndex],
      power_rating: Math.floor(Math.random() * 3) + 2,
      card_class: classes[Math.floor(Math.random() * classes.length)],
      image_base64: null, // No image for fallback
      is_fallback: true
    };
  };

  const handleBuyPack = async (pack) => {
    const userBalance = user?.slap_tokens || user?.coins || 0;
    
    if (userBalance < pack.cost) {
      toast.error('Not enough $SLAP!', {
        description: 'Deposit SOL and swap for $SLAP tokens'
      });
      return;
    }
    
    // Reset error state
    setPackOpeningError(null);
    setIsPurchasing(true);
    setIsOpening(true);
    
    // FAILSAFE TIMEOUT: If backend doesn't respond in 3 seconds, force reveal
    const timeoutId = setTimeout(() => {
      console.warn('Pack opening timeout - forcing reveal with fallback');
      setIsOpening(false);
      setIsPurchasing(false);
      
      // Generate a fallback card locally
      const fallbackCard = generateFallbackCard(pack.id);
      setPulledCard(fallbackCard);
      setShowPackAnimation(true);
      
      toast.warning('Connection slow - card generated locally!', {
        description: 'Your pack was opened, but we used a backup system.',
        duration: 5000
      });
      
      refreshUser().catch(() => {});
    }, 3000);
    
    try {
      const response = await axios.post(
        `${API}/shop/buy-pack?pack_id=${pack.id}&session_id=${SESSION_ID}`,
        {},
        { timeout: 2500 } // Axios timeout slightly less than failsafe
      );
      
      // Clear failsafe timeout since we got a response
      clearTimeout(timeoutId);
      
      if (response.data.success) {
        setPulledCard(response.data.card);
        setShowPackAnimation(true);
        
        // FIX: Use inventoryManager to save card (no image data)
        const card = response.data.card;
        if (card) {
          saveCard(card);
          console.log('[Shop] Card saved via inventoryManager:', card.id || card.character_name);
        }
        
        await refreshUser();
      } else {
        throw new Error(response.data.detail || 'Unknown error');
      }
      
    } catch (error) {
      // Clear failsafe timeout
      clearTimeout(timeoutId);
      
      console.error('Error buying pack:', error);
      
      // Check if it's a timeout or network error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout already handled by failsafe, but just in case
        const fallbackCard = generateFallbackCard(pack.id);
        setPulledCard(fallbackCard);
        setShowPackAnimation(true);
        toast.warning('Connection timeout - backup card generated!');
      } else {
        // Real error - show message
        setPackOpeningError(error.response?.data?.detail || 'Failed to purchase pack');
        toast.error(error.response?.data?.detail || 'Failed to purchase pack', {
          description: 'Please try again. Your balance was not charged.',
          duration: 5000
        });
      }
    } finally {
      setIsPurchasing(false);
      setIsOpening(false);
    }
  };
  
  const handleWatchAd = () => {
    if (freeRewardCooldown > 0) {
      toast.error(`Wait ${Math.floor(freeRewardCooldown / 60)}m ${freeRewardCooldown % 60}s for free reward`);
      return;
    }
    setShowWatchAd(true);
  };
  
  const handleAdComplete = async () => {
    setShowWatchAd(false);
    localStorage.setItem('lastFreeReward', Date.now().toString());
    setFreeRewardCooldown(300);
    
    // Award free $SLAP instead of shards
    const freeDegen = 50;
    
    try {
      // Update user degen via API
      await axios.post(`${API}/user/${SESSION_ID}/earn-slap?amount=${freeDegen}`);
      await refreshUser();
      
      toast.success(`+${freeDegen} $SLAP earned!`, {
        description: 'Use it to buy packs or enter the arena!',
        icon: '💰'
      });
      
    } catch (error) {
      console.error('Error claiming free reward:', error);
      toast.error('Failed to claim reward');
    }
  };
  
  const formatCooldown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const slapTokens = user?.slap_tokens || user?.coins || 0;
  
  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-20">
      {/* Cyberpunk background */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FF00FF20 1px, transparent 1px),
            linear-gradient(90deg, #00FFFF20 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
      
      {/* Ambient glow */}
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] bg-neon-pink/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      {/* Watch Ad Overlay */}
      <AnimatePresence>
        {showWatchAd && <WatchAdOverlay onComplete={handleAdComplete} />}
      </AnimatePresence>
      
      {/* GOD-TIER Pack Opening Animation */}
      <AnimatePresence>
        {showPackAnimation && pulledCard && (
          <GodTierPackOpening 
            card={pulledCard} 
            onComplete={() => {
              setShowPackAnimation(false);
              setPulledCard(null);
              setIsOpening(false);
            }} 
          />
        )}
      </AnimatePresence>
      
      {/* Loading Overlay for Pack Purchase */}
      <AnimatePresence>
        {isPurchasing && !showPackAnimation && (
          <motion.div
            className="fixed inset-0 z-[850] flex items-center justify-center bg-black/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-center">
              <motion.div
                className="w-20 h-20 mx-auto mb-6 border-4 border-neon-pink rounded-full border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <p className="font-anton text-2xl text-white mb-2">PURCHASING PACK...</p>
              <p className="font-space text-sm text-white/60">Connecting to server...</p>
              {packOpeningError && (
                <motion.p 
                  className="font-space text-sm text-red-400 mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Error: {packOpeningError}
                </motion.p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate('/')}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            
            {/* Currency Display - $SLAP Only */}
            <div className="flex gap-4">
              {/* $SLAP (Hard Currency) */}
              <motion.div
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/60 border-2 border-yellow-500/50"
                animate={{ boxShadow: ['0 0 10px rgba(255,215,0,0.2)', '0 0 20px rgba(255,215,0,0.4)', '0 0 10px rgba(255,215,0,0.2)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Coins className="w-5 h-5 text-yellow-400" />
                <span className="font-anton text-xl text-yellow-400" data-testid="slap-balance">
                  {slapTokens.toLocaleString()}
                </span>
                <span className="font-space text-xs text-white/60">$SLAP</span>
              </motion.div>
            </div>
          </div>
          
          <motion.h1
            className="font-anton text-5xl md:text-7xl uppercase tracking-tight text-center"
            style={{
              background: 'linear-gradient(180deg, #FFD700 0%, #FF00FF 50%, #00FFFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="shop-title"
          >
            SLAP SHOP
          </motion.h1>
          <p className="font-space text-base text-white/60 text-center mt-4 tracking-wider">
            $SLAP ONLY • OPEN PACKS • GET LEGENDARY CARDS
          </p>
        </div>
      </header>
      
      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex justify-center gap-4 mb-8">
          {['packs', 'tokens'].map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`font-anton text-lg uppercase px-8 py-3 transition-all ${
                activeTab === tab
                  ? 'bg-neon-pink text-white shadow-[0_0_30px_rgba(255,0,255,0.6)]'
                  : 'bg-black/40 border-2 border-white/20 text-white/60 hover:border-white/40'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'packs' ? <Package className="w-5 h-5 mr-2" /> : <Coins className="w-5 h-5 mr-2" />}
              {tab === 'packs' ? 'CARD PACKS' : 'BUY $SLAP'}
            </Button>
          ))}
        </div>
        
        {/* Packs Tab */}
        {activeTab === 'packs' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Free Reward - DISABLED */}
            <div className="mb-8">
              <motion.div
                className="relative p-6 rounded-2xl border-2 border-gray-600/50 bg-gradient-to-br from-gray-900/30 to-black overflow-hidden opacity-60"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gray-600/20 flex items-center justify-center">
                      <Gift className="w-8 h-8 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="font-anton text-2xl text-gray-500">FREE $SLAP</h3>
                      <p className="font-space text-sm text-white/40">Coming soon - Watch ads to earn free tokens!</p>
                    </div>
                  </div>
                  
                  <Button
                    disabled={true}
                    className="font-anton text-lg uppercase px-6 py-3 bg-gray-700 text-gray-500 cursor-not-allowed"
                    data-testid="free-slap-btn"
                  >
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    COMING SOON
                  </Button>
                </div>
              </motion.div>
            </div>
            
            {/* Pack Grid - USD FIRST with GLOW & PULSE */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packTypes.map((pack, index) => {
                const Icon = pack.icon;
                const userBalance = slapTokens;
                const canAfford = userBalance >= pack.cost;
                
                return (
                  <motion.div
                    key={pack.id}
                    className={`relative p-6 rounded-2xl border-2 overflow-hidden ${
                      canAfford ? 'cursor-pointer' : 'opacity-60'
                    }`}
                    style={{
                      borderColor: canAfford ? '#FF00FF' : '#333',
                      background: `linear-gradient(135deg, ${pack.gradient.replace('from-', '').replace('to-', ', ').replace(' ', '')}20)`,
                    }}
                    whileHover={canAfford ? { scale: 1.05 } : {}}
                    whileTap={canAfford ? { scale: 0.98 } : {}}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      boxShadow: canAfford ? [
                        `0 0 20px ${pack.glowColor}, 0 0 40px ${pack.glowColor}40`,
                        `0 0 40px ${pack.glowColor}, 0 0 80px ${pack.glowColor}60`,
                        `0 0 20px ${pack.glowColor}, 0 0 40px ${pack.glowColor}40`,
                      ] : 'none'
                    }}
                    transition={{ 
                      delay: index * 0.1,
                      boxShadow: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
                    }}
                    data-testid={`pack-${pack.id}`}
                  >
                    {/* Currency Badge */}
                    <div 
                      className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold"
                      style={{ 
                        backgroundColor: 'rgba(255,215,0,0.2)',
                        color: '#FFD700'
                      }}
                    >
                      💰 $SLAP
                    </div>
                    
                    {/* Icon with Pulse Animation */}
                    <motion.div 
                      className={`w-20 h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br ${pack.gradient} flex items-center justify-center`}
                      animate={canAfford ? { 
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          `0 0 20px ${pack.glowColor}`,
                          `0 0 40px ${pack.glowColor}`,
                          `0 0 20px ${pack.glowColor}`,
                        ]
                      } : {}}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    {/* Info */}
                    <h3 className="font-anton text-xl text-white text-center mb-2">{pack.name}</h3>
                    <p className="font-space text-xs text-white/60 text-center mb-4">{pack.description}</p>
                    
                    {/* USD PRICE FIRST - BIG */}
                    <div className="text-center mb-2">
                      <motion.span 
                        className="font-anton text-4xl text-white"
                        style={{ textShadow: `0 0 20px ${pack.glowColor}` }}
                        animate={canAfford ? { scale: [1, 1.02, 1] } : {}}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        {pack.usd}
                      </motion.span>
                    </div>
                    
                    {/* Token Amount - Small */}
                    <div className="flex items-center justify-center gap-1 mb-4">
                      <Coins className="w-4 h-4 text-yellow-400/70" />
                      <span className="font-space text-sm text-yellow-400/70">
                        ~{pack.cost} $SLAP
                      </span>
                    </div>
                    
                    {/* Buy Button */}
                    <Button
                      onClick={() => handleBuyPack(pack)}
                      disabled={!canAfford || isPurchasing}
                      className={`w-full font-anton uppercase ${
                        canAfford
                          ? `bg-gradient-to-r ${pack.gradient} hover:opacity-80 text-white`
                          : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {canAfford ? 'BUY PACK' : 'NEED $SLAP'}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Economy Info */}
            <div className="mt-8 p-4 bg-black/40 border border-white/10 rounded-xl">
              <h4 className="font-anton text-lg text-white mb-2">💡 HOW TO GET $SLAP</h4>
              <div className="grid md:grid-cols-2 gap-4 font-space text-sm text-white/60">
                <div>
                  <p className="text-yellow-400 font-bold">DEPOSIT & SWAP</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Deposit SOL to your wallet</li>
                    <li>Swap SOL → $SLAP (1% fee)</li>
                    <li>Use Jupiter built-in swap</li>
                  </ul>
                </div>
                <div>
                  <p className="text-green-400 font-bold">PLAY TO EARN</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Win SLAP BATTLES arena (90% pot)</li>
                    <li>Sell cards on marketplace</li>
                    <li>Trade rare cards for profit</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Tokens Tab - USD FIRST */}
        {activeTab === 'tokens' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tokenBundles.map((bundle, index) => (
                <motion.div
                  key={bundle.id}
                  className={`relative p-6 rounded-2xl border-2 overflow-hidden ${
                    bundle.popular ? 'border-neon-pink' : 'border-white/20'
                  }`}
                  style={{
                    background: bundle.popular 
                      ? 'linear-gradient(135deg, rgba(255,0,255,0.2) 0%, rgba(0,0,0,0.8) 100%)'
                      : 'rgba(0,0,0,0.6)',
                  }}
                  whileHover={{ scale: 1.03 }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    boxShadow: bundle.popular ? [
                      '0 0 20px rgba(255,0,255,0.3)',
                      '0 0 40px rgba(255,0,255,0.5)',
                      '0 0 20px rgba(255,0,255,0.3)',
                    ] : 'none'
                  }}
                  transition={{ 
                    delay: index * 0.1,
                    boxShadow: { duration: 2, repeat: Infinity }
                  }}
                  data-testid={`token-bundle-${bundle.id}`}
                >
                  {/* Popular badge or Custom Label */}
                  {(bundle.popular || bundle.label) && (
                    <div className="absolute -top-1 -right-1 bg-neon-pink px-3 py-1 rounded-bl-lg">
                      <span className="font-anton text-xs text-white">{bundle.label || 'POPULAR'}</span>
                    </div>
                  )}
                  
                  {/* USD PRICE FIRST - BIG */}
                  <div className="text-center mb-4">
                    <motion.span 
                      className="font-anton text-5xl text-white"
                      style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}
                      animate={bundle.popular ? { scale: [1, 1.05, 1] } : {}}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      {bundle.usdPrice}
                    </motion.span>
                    <p className="font-space text-xs text-white/50 mt-1">{bundle.priceDisplay}</p>
                  </div>
                  
                  {/* Token amount */}
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Coins className="w-8 h-8 text-yellow-400" />
                    </div>
                    <span className="font-anton text-3xl text-yellow-400">{bundle.tokens.toLocaleString()}</span>
                    <span className="block font-space text-sm text-white/60">$SLAP</span>
                    {bundle.bonus > 0 && (
                      <span className="block font-space text-sm text-neon-green mt-1">
                        +{bundle.bonus} BONUS!
                      </span>
                    )}
                  </div>
                  
                  {/* Buy Button */}
                  <Button
                    onClick={() => handleBuyTokens(bundle)}
                    className={`w-full font-anton text-xl ${
                      bundle.popular
                        ? 'bg-neon-pink hover:bg-neon-pink/80 text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white border-2 border-white/20'
                    }`}
                  >
                    BUY NOW
                  </Button>
                </motion.div>
              ))}
            </div>
            
            {/* Solana Info */}
            <div className="mt-8 p-4 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-purple-500/30 rounded-xl text-center">
              <p className="font-anton text-xl text-white mb-2">🔗 POWERED BY SOLANA</p>
              <p className="font-space text-sm text-white/60">
                Token purchases use your local player wallet. 
                Fast, cheap transactions on Solana blockchain.
              </p>
              <p className="font-space text-xs text-yellow-400 mt-2">
                ⚠️ Coming soon - Full token integration
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
